/** @jsxImportSource @emotion/react */
import React, {useEffect, useState} from 'react';
import Web3 from "web3";
import { Contract } from "web3-eth-contract";
import detectEthereumProvider from '@metamask/detect-provider';

// json
import OpenSeaAbi from "./erc1155.json";
import EthTerrestrialAbi from "./ethts.json";
import EthTGenesisMap from "./etht_genesis.json";

// styles
import * as MainContainerStyles from '../../styles/MainContainer';
import * as AppStyles from '../../styles/App';

// misc assets
import MintUfo from "../../../assets/img/transact/mint_ufo.png"
import MintContainer from "../../../assets/img/transact/mint_container.png"
import Planet1 from "../../../assets/img/planet_1.png"
import Skele from "../../../assets/img/skele.png"
import TxtBlob from "../../../assets/img/txt_blob.png"
import Meteor from "../../../assets/img/info_button_meteor.png"

// team assets
import Kye from "../../../assets/img/team/kye.png"

// tools
import * as svg from 'save-svg-as-png';



interface Props {};

interface Transaction {
  hash: string;
  link: string;
  status: string;
}

// UPDATE ON DEPLOY
const TOTAL_QUANTITY: number = 4269;

const UNIT_COST = 0.14;
const MAX_QUANTITY = 10;
const ETHT_CONTRACT_ADDRESS = "0xd65c5D035A35F41f31570887E3ddF8c3289EB920";
const OS_CONTRACT_ADDRESS = "0x495f947276749Ce646f68AC8c248420045cb7b5e";

// rinkeby
// const ETHT_CONTRACT_ADDRESS = "";
// const OS_CONTRACT_ADDRESS = "0x88B48F654c30e99bc2e4A1559b4Dcf1aD93FA656";

// mainnet
const CHAIN_ID = 1;

// rinkeby
// const CHAIN_ID = 4;


const BASE_GAS: number = 120000;
const INCREMENTAL_GAS: number = 5000;
const GAS_BUFFER: number = 110000;

function MainContainer(props: Props)  {

  const [account, setAccount] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [web3, setWeb3] = useState<Web3>();
  const [ethTerrestrialContract, setEthTerrestrialContract] = useState<Contract|null>(null);
  const [openSeaContract, setOpenSeaContract] = useState<Contract|null>(null);
  const [ethTGenesisMap, setEthTGenesisMap] = useState<{ [osss: string]: string }>({});
  const [ufoHasArrived, setUfoHasArrived] = useState<boolean>(false);
  const [mothershipHasArrived, setMothershipHasArrived] = useState<boolean>(false);
  const [minted, setMinted] = useState<number|null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [genesisTokenIds, setGenesisTokenIds] = useState<string[]>([]);
  const [beamUpMessage, setBeamUpMessage] = useState<string>('');

  const [mintTxs, setMintTxs] = useState<Transaction[]>([]);
  const [successfulTxs, setSuccessfulTxs] = useState<string[]>([]);
  const [failedTxs, setFailedTxs] = useState<string[]>([]);
  const [beamUpTxs, setBeamUpTxs] = useState<Transaction[]>([]);

  const [downloadTokenId, setDownloadTokenId] = useState<string>('');

  useEffect(() => {
    const init = async () => {
      const provider: any = await detectEthereumProvider();
      setWeb3(new Web3(provider));
    };
    init();
  }, []);

  // set contracts and metadata
  useEffect(() => {
    const init = async () => {
      try {
        if (web3 != null) {
          const ethTerrestrialAbi = JSON.parse(EthTerrestrialAbi);
          setEthTerrestrialContract(new web3.eth.Contract(ethTerrestrialAbi, ETHT_CONTRACT_ADDRESS));

          const openSeaAbi = JSON.parse(OpenSeaAbi);
          setOpenSeaContract(new web3.eth.Contract(openSeaAbi, OS_CONTRACT_ADDRESS));

          let ethTGenesisMap = JSON.parse(EthTGenesisMap);
          setEthTGenesisMap(ethTGenesisMap);
        }
      } catch (err) {
        console.error('Error setting contract!');
      }
    };
    init();
  }, [web3]);

  // get has arrived data and num minted
  useEffect(() => {
    if (ethTerrestrialContract != null) {
      const getNumMinted = async () => {
        let numMinted = await ethTerrestrialContract.methods.totalSupply().call();
        setMinted(numMinted);

        const ufoHasArrived = await ethTerrestrialContract.methods._UFOhasArrived().call();
        setUfoHasArrived(ufoHasArrived);

        const mothershipHasArrived = await ethTerrestrialContract.methods._mothershipHasArrived().call();
        setMothershipHasArrived(mothershipHasArrived);
      };
      getNumMinted();
    } else {
      console.log('Waiting for contract to be set before fetching num minted...');
    }
  }, [ethTerrestrialContract]);
  

  // update successful and failed txs
  useEffect(() => {
    let txs = mintTxs.map(tx => {return {...tx}});
    for (let i = 0; i < txs.length; i++) {
      let tx = txs[i];
      for (let j = 0; j < successfulTxs.length; j++) {
        if (successfulTxs[j] === tx.hash) {
          txs  = Object.assign([...txs], {
              [i]: {
                  hash: txs[i].hash,
                  link: txs[i].link,
                  status: 'success'
              }
            });
        }
      }
      

      for (let j = 0; j < failedTxs.length; j++) {
        if (failedTxs[j] === tx.hash) {
          txs = Object.assign([...txs], {
            [i]: {
                ...txs[i],
                status: 'error'
            }
          });
        setMintTxs(txs);
        }
      }
    }
    setMintTxs(txs);

  }, [successfulTxs, failedTxs]);

  // get accounts genesis token ids
  useEffect(() => {
    getGenesisTokenIds();
  }, [ethTGenesisMap, openSeaContract, account]);

  const getGenesisTokenIds = async () => {
    if (openSeaContract != null && account != null) {
      let tokenIds: string[] = Object.keys(ethTGenesisMap);
      let accounts: string[] = Array(tokenIds.length).fill(account);
      let tokenIdBalances: string[] = await openSeaContract.methods.balanceOfBatch(accounts, tokenIds).call();
      let ownedTokenIds: string[] = [];
      for (let i = 0; i < tokenIdBalances.length; i++) {
        if (tokenIdBalances[i] === "1") {
          ownedTokenIds.push(tokenIds[i]);
        }
      }
      setGenesisTokenIds(ownedTokenIds);
      if (ownedTokenIds.length > 0) {
        setBeamUpMessage('You have ' + String(ownedTokenIds.length) + ' OG EthTerrestrials to beam up.')
      } else {
        setBeamUpMessage('You have no OG EthTerrestrials to beam up.')
      }
    }
  }

  // update num minted every 3 seconds
  setInterval(async () => {
    if (ethTerrestrialContract != null) {
      const numMinted = await ethTerrestrialContract.methods.totalSupply().call();
      setMinted(numMinted);
    }
  }, 5000);

  const onClickConnect = async () => {
    setIsConnecting(true);

    const provider = await detectEthereumProvider();

    if (provider) {
      connectToWallet(provider);
    } else {
      console.error('Error getting provider, please install web3 wallet!');
      setIsConnecting(false);
    }
  }

  const connectToWallet = async (provider: any) => {
    if (provider !== window.ethereum) {
      console.error('Do you have multiple wallets installed?');
      setIsConnecting(false);
    }

    if (web3) {
      web3.eth.requestAccounts()
      .then((accounts: any) => {
        handleAccountsChanged(accounts);
        setIsConnecting(false);
      })
      .catch((err: any) => {
        setIsConnecting(false);
        if (err.code === 4001) {
          // EIP-1193 userRejectedRequest error
          // if this happens, the user rejected the connection request.
          console.log('Please connect to MetaMask.');
        } else {
          console.error(err);
        }
      });
      provider.on('accountsChanged', handleAccountsChanged);
    }
  }

  const handleAccountsChanged = (accounts: any) => {
    if (accounts.length === 0) {
      setAccount(null);
      console.log('Please connect to MetaMask.');
    } else if (accounts[0] !== account) {
      setAccount(accounts[0]);
    }
  }

  const mint = async () => {
    if (ethTerrestrialContract != null && account != null && web3 != null) {
      const chainId = await web3.eth.getChainId();
      if (chainId !== CHAIN_ID) {
        alert('Please connect to Ethereum mainnet before minting.');
        return;
      }

      // get mint fee
      let unitPrice = UNIT_COST;

      // get total cost of mint = mint fee * number to mint
      let totalCost = Web3.utils.toWei((unitPrice * quantity).toFixed(2), "ether");

      // estimate gas
      let gasEstimate = BASE_GAS + ((quantity - 1) * INCREMENTAL_GAS) + GAS_BUFFER; 

      // send tx
      ethTerrestrialContract.methods.abduct(quantity).send({
        from: account,
        value: totalCost,
        gas: gasEstimate,
      }).on('transactionHash', function(hash: any){
          setMintTxs([...mintTxs, {hash: hash, link: `https://etherscan.io/tx/${hash}`, status: 'pending'}]);
        })
        .on('receipt', function(receipt: any){
          setSuccessfulTxs(successfulTxs => [...successfulTxs, receipt.transactionHash]);
        })
        .on('confirmation', function(confirmationNumber: any, receipt: any){
          //
        })
        .on('error', function(error: any, receipt: any) {
          try {
            if (receipt) {
              setFailedTxs([...failedTxs, receipt.transactionHash]);
            } else {
              const data = JSON.parse(error.message.split('[ethjs-query] while formatting outputs from RPC ', 2)[1].split("'").join(''));
              const txHash: string = data.value.data.data.txHash;
              let inMintTxs: boolean = false;
              for (let i = 0; i < mintTxs.length; i++) {
                if (mintTxs[i].hash === txHash) {
                  inMintTxs = true;
                }
              }
              if (!inMintTxs) {
                setMintTxs([...mintTxs, {hash: txHash, link: `https://etherscan.io/tx/${txHash}`, status: 'pending'}]);
              }
              setFailedTxs([...failedTxs, txHash]);
            }
          } catch {};
        });
    } else {
      console.error('Contract or account not set, cannot mint!')
    }
  };

  const onIncreaseQuantity = () => {
    if (quantity + 1 >= MAX_QUANTITY) {
      setQuantity(MAX_QUANTITY);
    } else {
      setQuantity(quantity + 1);
    }
  }

  const onDecreaseQuantity = () => {
    if (quantity - 1 <= 1) {
      setQuantity(1);
    } else {
      setQuantity(quantity - 1);
    }
  }

  const beamUpGenesisTokens = async () => {
    await getGenesisTokenIds();
    if (genesisTokenIds.length === 0) {
      // no genesis tokens
    } else if (web3 != null && openSeaContract != null) {
      const chainId = await web3.eth.getChainId();
      if (chainId !== CHAIN_ID) {
        alert('Please connect to Ethereum mainnet before beaming up.');
        return;
      }
      
      let batchRequest = new web3.BatchRequest();

      

      for (let i = 0; i < genesisTokenIds.length; i++) {
        const gasEstimate = await openSeaContract.methods.safeTransferFrom(account, ETHT_CONTRACT_ADDRESS, BigInt(genesisTokenIds[i]), 1, new Uint8Array(0)).estimateGas({
          from: account});

        batchRequest.add(openSeaContract.methods.safeTransferFrom(account, ETHT_CONTRACT_ADDRESS, BigInt(genesisTokenIds[i]), 1, new Uint8Array(0)).send.request({
          from: account,
          gas: gasEstimate + 250000
        }));
      }
      batchRequest.execute();
      setBeamUpMessage('Beam up in progress...')
    }
  }

  const beamUpIsActive = () => {
    if (mothershipHasArrived && genesisTokenIds.length > 0) {
      return true;
    } else {
      return false;
    }
  }

  const saleIsActive = () => {
    if (ethTerrestrialContract) {
      if (ufoHasArrived && minted != null && Number(minted) < TOTAL_QUANTITY) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  const shouldAllowMint = () => {
    if (minted && saleIsActive() && Number(minted) + quantity <= TOTAL_QUANTITY) {
      return true;
    }
    return false;
  }

  const b64_to_utf8 = (data: any) => {
    return decodeURIComponent(escape(window.atob(data)));
  };

  const downloadTokenImage = async () => {
    if (downloadTokenId === '' || parseInt(downloadTokenId) < 0 || parseInt(downloadTokenId) > 4269) {
      alert('Please enter a valid tokenId.');
      return;
    }
    if (ethTerrestrialContract) {
      const tokenData = await ethTerrestrialContract.methods.tokenURI(downloadTokenId).call();
      var decode = JSON.parse(
        b64_to_utf8(tokenData.substring(tokenData.indexOf(",") + 1))
      );
      var svgElement = b64_to_utf8(decode.image.replace('data:image/svg+xml;base64,', ''))

      var oParser = new DOMParser();
      var oDOM = oParser.parseFromString(svgElement, "image/svg+xml");

      svg.saveSvgAsPng(oDOM.documentElement, downloadTokenId + ".png");
    }
  };


  return (
    <MainContainerStyles.Content>
      <MainContainerStyles.Row>
        <MainContainerStyles.LeftPanel>
          <img src={MintUfo} alt="mint ufo" css={MainContainerStyles.mintUfo}/>
          <img src={MintContainer} alt="mint container" css={MainContainerStyles.mintContainer}/>
          <MainContainerStyles.MintButtonContainer>
            <img src={Skele} alt="spooky eth t skeleton" css={MainContainerStyles.skeleOne}/>
            <img src={Skele} alt="spooky eth t skeleton" css={MainContainerStyles.skeleTwo}/>
            <img src={Meteor} alt="bye bye dinos" css={MainContainerStyles.meteor}/>
            <img src={Planet1} alt="a planet with special rocks" css={MainContainerStyles.planetOne}/>
            <MainContainerStyles.Row>
                <MainContainerStyles.MintInfoContainer>
                  <label css={[MainContainerStyles.mintInfoTxt, AppStyles.txt]}>
                      <svg css={[MainContainerStyles.ethLogo]} aria-hidden="true" focusable="false" data-prefix="fab" data-icon="ethereum" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M311.9 260.8L160 353.6 8 260.8 160 0l151.9 260.8zM160 383.4L8 290.6 160 512l152-221.4-152 92.8z"></path></svg>
                      {UNIT_COST} each
                  </label>
                </MainContainerStyles.MintInfoContainer>
              </MainContainerStyles.Row>
            
              { !account && !isConnecting &&
              <MainContainerStyles.Row>
                <input type="submit" onClick={() => onClickConnect()} alt="mint button" css={[MainContainerStyles.divImg, MainContainerStyles.mintButton, AppStyles.txt]} value="Connect"/>
              </MainContainerStyles.Row>  

              }
              { !account && isConnecting &&
                <MainContainerStyles.Row>
                  <input type="submit" alt="mint button" css={[MainContainerStyles.divImg, MainContainerStyles.mintButton, AppStyles.txt]} value="Connect"/>
                </MainContainerStyles.Row>
              }

              { account && 
              <MainContainerStyles.Row>
                <input type="submit" onClick={() => onDecreaseQuantity()} alt="descrease quantity" css={[MainContainerStyles.divImg, MainContainerStyles.decreaseQuantityButton]} value="-" disabled={!saleIsActive()}/>
                <input type="submit" onClick={() => mint()} alt="mint button" css={[MainContainerStyles.divImg, MainContainerStyles.mintButton, AppStyles.txt]} value={'Mint ' + quantity} disabled={!shouldAllowMint()}/>
                <input type="submit" onClick={() => onIncreaseQuantity()} alt="increase quantity" css={[MainContainerStyles.divImg, MainContainerStyles.increaseQuantityButton]} value="+" disabled={!saleIsActive()}/>
              </MainContainerStyles.Row>  
              }
            <MainContainerStyles.Row>
            <MainContainerStyles.MintInfoContainer>
              <label css={[MainContainerStyles.mintInfoTxt, AppStyles.txt]}>{minted}/{TOTAL_QUANTITY} minted</label>
            </MainContainerStyles.MintInfoContainer>
            </MainContainerStyles.Row>
            <MainContainerStyles.Row>
            <label css={[MainContainerStyles.txInfoTxt, AppStyles.txt]}>tx submitted: { mintTxs.length === 0 && 'None' } 
              {mintTxs.length > 0 && 
              mintTxs.map((tx) =>  
                tx.status === 'pending' ?
                  <a css={[MainContainerStyles.txInfoTxt, MainContainerStyles.txLinkPending]} href={tx.link} target="_blank" rel="noopener noreferrer">  0x...{tx.link.slice(-3)} </a>
                  :
                tx.status === 'success' ?
                  <a css={[MainContainerStyles.txInfoTxt, MainContainerStyles.txLinkSuccess]} href={tx.link} target="_blank" rel="noopener noreferrer">  0x...{tx.link.slice(-3)}&#10004;</a>
                  : 
                  <a css={[MainContainerStyles.txInfoTxt, MainContainerStyles.txLinkError]} href={tx.link} target="_blank" rel="noopener noreferrer">  0x...{tx.link.slice(-3)}&#x2717;</a>
                )}
              </label>
            </MainContainerStyles.Row>
          </MainContainerStyles.MintButtonContainer>
        </MainContainerStyles.LeftPanel>
        <MainContainerStyles.RightPanel>
          { !account && !isConnecting &&
            <input type="submit" onClick={() => onClickConnect()} alt="connect" css={[MainContainerStyles.divImg, MainContainerStyles.beamUpContainer, AppStyles.txt]} value="Connect"/>
          }
          { !account && isConnecting &&
            <input type="submit" alt="connect" css={[MainContainerStyles.divImg, MainContainerStyles.beamUpContainer, AppStyles.txt]} value="Connect"/>
          }
          { account && 
            <div>
              <input type="submit" onClick={() => beamUpGenesisTokens()} alt="beam up button" css={[MainContainerStyles.divImg, MainContainerStyles.beamUpContainer, AppStyles.txt]} value="Beam Up" disabled={!beamUpIsActive()}/>
              <a css={[AppStyles.txt, MainContainerStyles.learnMore, MainContainerStyles.learnMoreBeamUp]} href="https://ethterrestrials.gitbook.io/ethterrestrials/art/og-collection#how-to-beam-up" target="_blank" rel="noopener noreferrer">Learn more</a>
              <span css={[AppStyles.txt, MainContainerStyles.beamUpMessage]}>{beamUpMessage}</span>
            </div>
          }          
        </MainContainerStyles.RightPanel>
        
      </MainContainerStyles.Row>
      
      <MainContainerStyles.Row>
        <MainContainerStyles.TxtBlob>
          <img id="about" src={TxtBlob} alt="about ethterrestrials" css={MainContainerStyles.divImg}/>
          <div css={[AppStyles.txt, MainContainerStyles.aboutTxt]}> 
          <p>The First EtherTerrestrials (ETH Ts) came to Planet Earth 42,069 years ago, many years from now, in the Future Past.</p>
          <p>Seeking refuge from the Centralized Creation Authority, which had decreed that no conscious life forms could be algorithmically generated from organic organisms on foreign worlds,  they dipped into our dimension where they discovered an unknown rock, floating in the middle of outer space, which was, somehow, already teeming with life.</p>
          <p>Earth, now the only planet in the known Universe with naturally occurring cannabinoids, ergot, and psilocybin, was the perfect environment for the ETH Ts to experiment with the evolution of consciousness.</p>
          <p>They knew the only way to preserve their legacy was to sacrifice their physical form, and place themselves on chain, where they could live forever, until such time as they could be discovered by a future generation of inter-galactic pscyhanaut.</p>
          <p><b>No roadmap</b>, they have achieved their goal of getting on-chain and are now free to be used in whatever way their holders see fit.&nbsp;<a css={[AppStyles.txt, MainContainerStyles.learnMore]} href="https://ethterrestrials.gitbook.io/ethterrestrials/" target="_blank" rel="noopener noreferrer">Learn more</a></p>
          </div>
        </MainContainerStyles.TxtBlob>
        <MainContainerStyles.TeamContainer>
          <MainContainerStyles.Row>
            <MainContainerStyles.Team>
              <MainContainerStyles.TeamItem>
                <div id="team" css={[AppStyles.header, AppStyles.txt, MainContainerStyles.by]}>By,</div>
              </MainContainerStyles.TeamItem>
              <MainContainerStyles.TeamItem>
                <img src={Kye} alt="kye" css={[MainContainerStyles.divImg, MainContainerStyles.teamImg]}/>
                <div css={[AppStyles.txt, MainContainerStyles.teamTxt]}>Kye</div>
              </MainContainerStyles.TeamItem>
            </MainContainerStyles.Team>
          </MainContainerStyles.Row>
        </MainContainerStyles.TeamContainer>
      </MainContainerStyles.Row>
      <MainContainerStyles.Row id="links" css={[MainContainerStyles.links]}>
            <img src={Meteor} alt="another meteor crap" css={MainContainerStyles.meteorTwo}/>
            <a href="https://opensea.io/collection/ethterrestrials"  css={[MainContainerStyles.openSeaLink]} target="_blank" rel="noopener noreferrer"><svg width="4vw" height="6vw" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.4414 13.5517C17.4 13.5103 17.3379 13.5103 17.2759 13.531L17.131 13.5931C16.4276 13.9448 15.6414 14.1103 14.8552 14.1103C14.8138 14.1103 14.7931 14.131 14.7931 14.1517C14.7103 14.5241 14.4 14.7931 14.0069 14.7931H12.3931V13.4483H12.4138C12.5172 13.469 12.6 13.469 12.7034 13.4897C13.3862 13.5931 13.7172 13.9034 13.8621 14.0483L13.8828 14.069C13.9034 14.0897 13.9448 14.0897 13.9655 14.069C13.9862 14.0483 14.0069 14.0276 14.0483 14.0069C14.2966 13.8414 14.8759 13.4897 14.8759 11.6276C14.8759 9.72414 13.4276 8.44138 13.2621 8.35862L12.4138 8.29655H12.3931V7.75862C12.5586 7.65517 12.6828 7.46897 12.6828 7.24138C12.6828 6.91035 12.4345 6.64138 12.1034 6.64138C11.7724 6.64138 11.5241 6.91035 11.5241 7.24138C11.5241 7.44828 11.6276 7.63448 11.8138 7.73793V8.1931L10.5931 8.06897C10.531 8.06897 10.4897 8.13103 10.531 8.17241C10.7379 8.50345 11.2966 9.53793 11.3379 11.0897C11.3793 12.5379 11.1517 13.2207 11.0483 13.469C11.0276 13.4897 11.0276 13.5103 11.0483 13.531C11.069 13.5517 11.0897 13.5517 11.1103 13.5517C11.2552 13.531 11.5034 13.4897 11.8138 13.469V14.8138H10.469C10.0345 14.8138 9.66207 14.4414 9.66207 14.0069V13.6759C9.66207 13.6345 9.64138 13.6138 9.6 13.6138H7.09655C7.07586 13.6138 7.03448 13.6345 7.03448 13.6759C7.03448 13.7379 7.03448 13.8 7.03448 13.8621C7.03448 14.7931 7.30345 15.6414 7.75862 16.3862C7.84138 16.5103 7.98621 16.5724 8.11034 16.5103L8.37931 16.3862C8.58621 16.2828 8.83448 16.2828 9.06207 16.3862L9.35172 16.5103C9.55862 16.6138 9.8069 16.6138 10.0345 16.5103L10.3241 16.3862C10.5517 16.2828 10.7793 16.2828 11.0069 16.3862L11.2966 16.5103C11.5241 16.6138 11.7517 16.6138 11.9793 16.5103L12.269 16.3862C12.4966 16.2828 12.7241 16.2828 12.9517 16.3862L13.2414 16.5103C13.469 16.6138 13.6966 16.6138 13.9241 16.5103L14.2138 16.3862C14.4414 16.2828 14.669 16.2828 14.8966 16.3862L15.1862 16.5103C15.3724 16.5931 15.5793 16.4897 15.6828 16.3034C15.8483 16.0345 15.9931 15.7241 16.0966 15.4345C16.3241 14.7517 16.7379 14.1103 17.3793 13.8C17.4414 13.7793 17.4621 13.7172 17.4621 13.6552C17.5034 13.6345 17.4828 13.5724 17.4414 13.5517ZM8.21379 13.0966H10.5724C10.6138 13.0966 10.6345 13.0759 10.6345 13.0345V10.469C10.6345 10.4483 10.6138 10.4276 10.5931 10.4069L9.43448 9.86897C9.41379 9.84828 9.37241 9.86897 9.35172 9.91035L8.15172 12.9931C8.13103 13.0552 8.17241 13.0966 8.21379 13.0966ZM12 0C5.37931 0 0 5.37931 0 12C0 18.6207 5.37931 24 12 24C18.6207 24 24 18.6207 24 12C24 5.37931 18.6207 0 12 0ZM11.9586 20.731C7.17931 20.731 3.31034 16.8621 3.31034 12.0621C3.31034 7.28276 7.17931 3.3931 11.9586 3.3931C16.7379 3.3931 20.6069 7.28276 20.6069 12.0621C20.5862 16.8621 16.7172 20.731 11.9586 20.731ZM17.2759 13.531L17.131 13.5931C16.4276 13.9448 15.6414 14.1103 14.8552 14.1103C14.8138 14.1103 14.7931 14.131 14.7931 14.1517C14.7103 14.5241 14.4 14.7931 14.0069 14.7931H12.3931V13.4483H12.4138C12.5172 13.469 12.6 13.469 12.7034 13.4897C13.3862 13.5931 13.7172 13.9034 13.8621 14.0483L13.8828 14.069C13.9034 14.0897 13.9448 14.0897 13.9655 14.069C13.9862 14.0483 14.0069 14.0276 14.0483 14.0069C14.2966 13.8414 14.8759 13.4897 14.8759 11.6276C14.8759 9.72414 13.4276 8.44138 13.2621 8.35862L12.4138 8.29655H12.3931V7.75862C12.5586 7.65517 12.6828 7.46897 12.6828 7.24138C12.6828 6.91035 12.4345 6.64138 12.1034 6.64138C11.7724 6.64138 11.5241 6.91035 11.5241 7.24138C11.5241 7.44828 11.6276 7.63448 11.8138 7.73793V8.1931L10.5931 8.06897C10.531 8.06897 10.4897 8.13103 10.531 8.17241C10.7379 8.50345 11.2966 9.53793 11.3379 11.0897C11.3793 12.5379 11.1517 13.2207 11.0483 13.469C11.0276 13.4897 11.0276 13.5103 11.0483 13.531C11.069 13.5517 11.0897 13.5517 11.1103 13.5517C11.2552 13.531 11.5034 13.4897 11.8138 13.469V14.8138H10.469C10.0345 14.8138 9.66207 14.4414 9.66207 14.0069V13.6759C9.66207 13.6345 9.64138 13.6138 9.6 13.6138H7.09655C7.07586 13.6138 7.03448 13.6345 7.03448 13.6759C7.03448 13.7379 7.03448 13.8 7.03448 13.8621C7.03448 14.7931 7.30345 15.6414 7.75862 16.3862C7.84138 16.5103 7.98621 16.5724 8.11034 16.5103L8.37931 16.3862C8.58621 16.2828 8.83448 16.2828 9.06207 16.3862L9.35172 16.5103C9.55862 16.6138 9.8069 16.6138 10.0345 16.5103L10.3241 16.3862C10.5517 16.2828 10.7793 16.2828 11.0069 16.3862L11.2966 16.5103C11.5241 16.6138 11.7517 16.6138 11.9793 16.5103L12.269 16.3862C12.4966 16.2828 12.7241 16.2828 12.9517 16.3862L13.2414 16.5103C13.469 16.6138 13.6966 16.6138 13.9241 16.5103L14.2138 16.3862C14.4414 16.2828 14.669 16.2828 14.8966 16.3862L15.1862 16.5103C15.3724 16.5931 15.5793 16.4897 15.6828 16.3034C15.8483 16.0345 15.9931 15.7241 16.0966 15.4345C16.3241 14.7517 16.7379 14.1103 17.3793 13.8C17.4414 13.7793 17.4621 13.7172 17.4621 13.6552C17.4621 13.6138 17.4414 13.5517 17.4 13.531C17.4 13.531 17.3379 13.5103 17.2759 13.531ZM8.21379 13.0966H10.5724C10.6138 13.0966 10.6345 13.0759 10.6345 13.0345V10.469C10.6345 10.4483 10.6138 10.4276 10.5931 10.4069L9.43448 9.86897C9.41379 9.84828 9.37241 9.86897 9.35172 9.91035L8.15172 12.9931C8.13103 13.0552 8.17241 13.0966 8.21379 13.0966Z" fill="currentColor"></path></svg></a>
            <a href="https://looksrare.org/collections/0xd65c5D035A35F41f31570887E3ddF8c3289EB920"  css={[MainContainerStyles.looksRareLink]} target="_blank" rel="noopener noreferrer">
              <svg width="4vw" height="6vw" viewBox="0 0 148 148" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M33.379 43.7306C55.8133 21.4231 92.1867 21.4231 114.621 43.7306L134 63L114.621 82.2694C92.1867 104.577 55.8134 104.577 33.379 82.2694L14 63L33.379 43.7306Z" fill="black"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M0 63.0304L44 19H104L148 63.0304L74 137L0 63.0304ZM108 46.9998C89.3047 28.2224 58.6953 28.2225 40 46.9999L24 63.0001L40 79.0002C58.6953 97.7776 89.3047 97.7775 108 79.0001L124 63.0001L108 46.9998Z" fill="#0CE466"/>
                <path d="M74 77C66.2712 77 60 70.7351 60 63C60 55.2649 66.2712 49 74 49C81.7288 49 88 55.2649 88 63C88 70.7351 81.7288 77 74 77Z" fill="black"/>
                <path fill-rule="evenodd" clip-rule="evenodd" d="M51 63C51 75.7077 61.3026 86 74 86C86.6974 86 97 75.7077 97 63C97 50.2923 86.6974 40 74 40C61.3026 40 51 50.2923 51 63ZM64 63C64 68.5251 68.4794 73 74 73C79.5206 73 84 68.5251 84 63C84 57.4749 79.5206 53 74 53C68.4794 53 64 57.4749 64 63Z" fill="white"/>
              </svg>
            </a>
            <a href="https://discord.gg/gnaVUJNufY"  css={[MainContainerStyles.discordLink]} target="_blank" rel="noopener noreferrer"><svg width="4vw" height="6vw" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20.317 2.36985C18.761 1.65561 17.119 1.1463 15.4319 0.854651C15.4166 0.851792 15.4007 0.853838 15.3867 0.860499C15.3726 0.86716 15.3609 0.878096 15.3534 0.891751C15.1424 1.26705 14.9087 1.75655 14.7451 2.14125C12.9004 1.86505 11.0651 1.86505 9.2583 2.14125C9.0947 1.74795 8.8525 1.26705 8.6406 0.891751C8.63276 0.878407 8.62107 0.867739 8.60706 0.861138C8.59306 0.854538 8.57739 0.852312 8.5621 0.854751C6.87488 1.14572 5.2327 1.65499 3.6769 2.36975C3.66359 2.37544 3.65238 2.38512 3.6448 2.39745C0.533404 7.04585 -0.318996 11.58 0.099204 16.0579C0.100386 16.0688 0.103757 16.0794 0.109116 16.0891C0.114476 16.0987 0.121714 16.1072 0.130404 16.1139C2.1832 17.6215 4.1717 18.5368 6.1233 19.1434C6.13848 19.1479 6.15468 19.1477 6.16972 19.1427C6.18477 19.1378 6.19795 19.1284 6.2075 19.1158C6.6691 18.4854 7.0806 17.8206 7.4335 17.1216C7.43835 17.112 7.44112 17.1015 7.44163 17.0908C7.44213 17.0801 7.44036 17.0693 7.43643 17.0594C7.4325 17.0494 7.4265 17.0403 7.41883 17.0328C7.41115 17.0253 7.40198 17.0196 7.3919 17.0159C6.7391 16.7683 6.1176 16.4664 5.5197 16.1236C5.5088 16.1171 5.49964 16.1082 5.49304 16.0974C5.48643 16.0866 5.48258 16.0744 5.48183 16.0617C5.48108 16.0491 5.48345 16.0365 5.48873 16.025C5.49401 16.0135 5.50204 16.0035 5.5121 15.9959C5.6379 15.9016 5.7638 15.8035 5.8839 15.7044C5.89458 15.6956 5.9075 15.69 5.92121 15.6881C5.93492 15.6863 5.94887 15.6883 5.9615 15.6939C9.8893 17.4872 14.1415 17.4872 18.0229 15.6939C18.0355 15.6879 18.0496 15.6856 18.0635 15.6873C18.0774 15.689 18.0906 15.6946 18.1014 15.7035C18.2216 15.8025 18.3474 15.9016 18.4742 15.9959C18.4843 16.0034 18.4924 16.0134 18.4977 16.0248C18.5031 16.0362 18.5055 16.0488 18.5049 16.0614C18.5042 16.074 18.5005 16.0863 18.494 16.0971C18.4875 16.1079 18.4784 16.117 18.4676 16.1235C17.8695 16.4728 17.2429 16.771 16.5946 17.0149C16.5845 17.0187 16.5754 17.0246 16.5678 17.0323C16.5602 17.0399 16.5542 17.049 16.5504 17.0591C16.5466 17.0692 16.5449 17.0799 16.5455 17.0907C16.5461 17.1015 16.549 17.112 16.5539 17.1216C16.9143 17.8196 17.3258 18.4844 17.7789 19.1148C17.7882 19.1278 17.8013 19.1375 17.8164 19.1427C17.8315 19.1478 17.8478 19.148 17.8631 19.1434C19.8241 18.5366 21.8126 17.6215 23.8654 16.1139C23.8742 16.1075 23.8816 16.0992 23.887 16.0897C23.8924 16.0802 23.8957 16.0696 23.8967 16.0588C24.3971 10.8818 23.0585 6.38485 20.3482 2.39835C20.3416 2.3854 20.3305 2.37525 20.317 2.36975V2.36985ZM8.02 13.3313C6.8375 13.3313 5.8631 12.2456 5.8631 10.9123C5.8631 9.57905 6.8186 8.49335 8.0201 8.49335C9.2309 8.49335 10.1958 9.58855 10.1769 10.9124C10.1769 12.2456 9.2214 13.3313 8.02 13.3313ZM15.9948 13.3313C14.8123 13.3313 13.8379 12.2456 13.8379 10.9123C13.8379 9.57905 14.7933 8.49335 15.9948 8.49335C17.2056 8.49335 18.1705 9.58855 18.1516 10.9124C18.1516 12.2456 17.2056 13.3313 15.9948 13.3313Z" fill="currentColor"></path></svg></a>
            <a href="https://twitter.com/ETHTerrestrials" css={[MainContainerStyles.twitterLink]} target="_blank" rel="noopener noreferrer"><svg width="4vw" height="6vw" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M23.953 2.56997C23.0545 2.96557 22.1026 3.2267 21.128 3.34496C22.1541 2.72822 22.9224 1.76101 23.291 0.621965C22.34 1.17697 21.286 1.58097 20.164 1.80597C19.4238 1.01439 18.4429 0.489386 17.3736 0.312467C16.3044 0.135547 15.2067 0.316616 14.2509 0.827556C13.2952 1.3385 12.5349 2.15072 12.0881 3.13809C11.6412 4.12546 11.533 5.23273 11.78 6.28797C7.69 6.09497 4.067 4.12996 1.64 1.16197C1.19879 1.91178 0.968664 2.76699 0.974 3.63697C0.974 5.34697 1.844 6.84997 3.162 7.73296C2.38086 7.7081 1.61697 7.4969 0.934 7.11696V7.17697C0.933557 8.31336 1.32627 9.4149 2.04551 10.2947C2.76474 11.1745 3.76621 11.7784 4.88 12.004C4.1583 12.1973 3.40241 12.2264 2.668 12.089C2.98412 13.067 3.59766 13.9219 4.42301 14.5345C5.24837 15.147 6.24435 15.4867 7.272 15.506C5.53153 16.8719 3.38248 17.6133 1.17 17.611C0.78 17.611 0.391 17.588 0 17.544C2.25571 18.9883 4.87851 19.755 7.557 19.753C16.61 19.753 21.555 12.257 21.555 5.76797C21.555 5.55797 21.555 5.34797 21.54 5.13797C22.506 4.44283 23.3392 3.57978 24 2.58997L23.953 2.56997Z" fill="currentColor"></path></svg></a>
            <a href="https://github.com/sum1eth/ETHTerrestrials" css={[MainContainerStyles.githubLink]} target="_blank" rel="noopener noreferrer"><svg width="6vw" height="7vw" aria-hidden="true" viewBox="0 0 24 20" version="1.1" data-view-component="true"><path fill-rule="evenodd" d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"></path></svg></a>
      </MainContainerStyles.Row>
      <MainContainerStyles.Row css={[]}>
      <input css={MainContainerStyles.donwloadInput} type="text" value={downloadTokenId} onChange={(e) => setDownloadTokenId(e.target.value)} placeholder="tokenId"/>
      <input type="submit" onClick={() => downloadTokenImage()} alt="download button" css={[MainContainerStyles.divImg, MainContainerStyles.downloadButton, AppStyles.txt]} value="Download" />

      </MainContainerStyles.Row>
    </MainContainerStyles.Content>
  )
};

export { MainContainer };